import { useEffect, useState } from "react";
import {
  ApiRequestError,
  getLockedFields,
  putLockedFields,
} from "../lib/client";
import type { LockedFieldResource } from "../lib/types";
import {
  LOCKED_FIELD_RESOURCE_LABELS,
  LOCKED_FIELD_RESOURCES,
} from "../lib/types";

type Status =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; message: string };

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; lockable: string[]; saved: string[] };

/**
 * Make a label out of a field name.
 *
 * The name is the path the web client uses, for example
 * `contactInfo.physicalAddresses.postalCode`. The last segment is the field,
 * and the segment before it gives the group. Derivation keeps this list in
 * agreement with ps-main, which a second hand-written list would not.
 */
export function humanize(fieldName: string): string {
  const words = (segment: string) =>
    segment
      .replace(/_/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .toLowerCase();

  const segments = fieldName.split(".");
  const field = words(segments[segments.length - 1] as string);
  if (segments.length < 2) return field;
  return `${words(segments[segments.length - 2] as string)} · ${field}`;
}

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

/**
 * Show and set the fields this subscriber locks, one resource at a time.
 *
 * A locked field reads as read-only in the web client, on every constituent
 * that an integration owns. The set belongs to the subscriber, not to one
 * constituent. The web client leaves a constituent with no integration record
 * fully editable, because no external system holds a value for it.
 *
 * Saving writes the whole set. ps-main deletes every locked field of the
 * resource first, so a box that is clear becomes editable again. The check
 * boxes come from ps-main's own list of lockable fields, so a name that locks
 * nothing cannot be entered here.
 */
export function LockedFields({ subscriberId }: { subscriberId: string }) {
  const [resource, setResource] = useState<LockedFieldResource>("person");
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [selected, setSelected] = useState<string[]>([]);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: "loading" });
    setSelected([]);
    setStatus({ kind: "idle" });
    getLockedFields(subscriberId, resource)
      .then((view) => {
        if (cancelled) return;
        setLoad({
          kind: "ready",
          lockable: view.lockableFields,
          saved: view.lockedFields,
        });
        setSelected(view.lockedFields);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoad({
          kind: "error",
          message:
            err instanceof ApiRequestError
              ? err.message
              : "Failed to load the locked fields.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [subscriberId, resource]);

  function toggle(fieldName: string) {
    setSelected((prev) =>
      prev.includes(fieldName)
        ? prev.filter((name) => name !== fieldName)
        : [...prev, fieldName].sort(),
    );
    if (status.kind === "saved" || status.kind === "error") {
      setStatus({ kind: "idle" });
    }
  }

  async function save() {
    setStatus({ kind: "saving" });
    try {
      const saved = await putLockedFields(subscriberId, resource, selected);
      setLoad((prev) => (prev.kind === "ready" ? { ...prev, saved } : prev));
      setSelected(saved);
      setStatus({ kind: "saved" });
    } catch (err) {
      setStatus({
        kind: "error",
        message:
          err instanceof ApiRequestError
            ? err.message
            : "Failed to save the locked fields.",
      });
    }
  }

  const dirty = load.kind === "ready" && !sameSet(selected, load.saved);
  const busy = status.kind === "saving" || load.kind === "loading";

  return (
    <section className="panel">
      <h2 className="section-head">Locked fields</h2>
      <p className="hint">
        A locked field reads as read-only in the web client, on every
        constituent that an integration owns. A constituent with no integration
        record stays editable. Saving writes the whole set, so a box that is
        clear makes the field editable again.
      </p>
      {resource === "social_link" && (
        <p className="hint">
          On social links, descriptor is the job title. Locking it makes the job
          title read-only on employee links. Family, general, and employer links
          stay editable.
        </p>
      )}

      <div className="toolbar">
        {LOCKED_FIELD_RESOURCES.map((option) => (
          <button
            key={option}
            type="button"
            className={`btn${resource === option ? " primary" : ""}`}
            disabled={status.kind === "saving"}
            onClick={() => setResource(option)}
          >
            {LOCKED_FIELD_RESOURCE_LABELS[option]}
          </button>
        ))}
      </div>

      {load.kind === "loading" && (
        <p className="hint">Loading locked fields…</p>
      )}
      {load.kind === "error" && (
        <div className="banner error">{load.message}</div>
      )}

      {load.kind === "ready" && (
        <>
          <div className="chip-grid">
            {load.lockable.map((fieldName) => (
              <label key={fieldName} className="chip-check">
                <input
                  type="checkbox"
                  checked={selected.includes(fieldName)}
                  disabled={busy}
                  onChange={() => toggle(fieldName)}
                />
                {humanize(fieldName)}
                <code className="chip-code">{fieldName}</code>
              </label>
            ))}
          </div>

          <div className="toolbar">
            <button
              type="button"
              className="btn primary"
              onClick={save}
              disabled={busy || !dirty}
            >
              {status.kind === "saving"
                ? "Saving…"
                : `Save ${selected.length} locked field${
                    selected.length === 1 ? "" : "s"
                  }`}
            </button>
            {status.kind === "saved" && (
              <span className="hint ok-text">Saved.</span>
            )}
            {dirty && status.kind !== "saving" && (
              <span className="hint">Unsaved changes.</span>
            )}
          </div>
        </>
      )}

      {status.kind === "error" && (
        <div className="banner error">{status.message}</div>
      )}
    </section>
  );
}
